import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { fetchProjects } from "../../api/projects";
import type { ProjectSummary } from "../../api/projects";
import {
  fetchPriceVariance,
  fetchReferenceQualitySummary,
  fetchPriceVarianceGrouped,
  fetchUserVariance,
} from "../../api/reports";
import type {
  PriceVarianceFilters,
  VarianceGroupBy,
  VarianceGroupedSort,
  VarianceSort,
} from "../../api/reports";
import type { MultiSelectOption } from "../../components/MultiSelectChips";
import { filterColor } from "../../lib/filterColors";
import { formatEuro } from "../../lib/format";
import { projectDisplay, projectSearchText } from "../../lib/projectDisplay";

import { PAGE_SIZE } from "./constants";
import type { GroupMode } from "./types";

export function usePriceVarianceReportData({
  enabled,
  rollupsEnabled = enabled,
  effectiveFilters,
  groupMode,
  sort,
  groupedSort,
}: {
  enabled: boolean;
  /** Manufacturer/supplier rollups also feed the Analyse charts and filter options. */
  rollupsEnabled?: boolean;
  effectiveFilters: PriceVarianceFilters;
  groupMode: GroupMode;
  sort: VarianceSort;
  groupedSort: VarianceGroupedSort;
}) {
  const flatEnabled = enabled && groupMode === "none";
  const groupedEnabled = enabled && groupMode !== "none";

  const referenceSummaryQuery = useQuery({
    queryKey: ["reports", "reference-quality", effectiveFilters],
    queryFn: () => fetchReferenceQualitySummary(effectiveFilters),
    enabled,
  });

  const varianceQuery = useInfiniteQuery({
    queryKey: ["reports", "price-variance", effectiveFilters, sort],
    queryFn: ({ pageParam }) =>
      fetchPriceVariance({
        ...effectiveFilters,
        sort,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
    enabled: flatEnabled,
  });

  const groupedQuery = useQuery({
    queryKey: [
      "reports",
      "price-variance-grouped",
      effectiveFilters,
      groupMode,
      groupedSort,
    ],
    queryFn: () =>
      fetchPriceVarianceGrouped({
        ...effectiveFilters,
        group_by: groupMode as VarianceGroupBy,
        sort: groupedSort,
      }),
    enabled: groupedEnabled,
  });

  const usersQuery = useQuery({
    queryKey: ["reports", "users", "variance"],
    queryFn: () => fetchUserVariance(500, 0),
    staleTime: 300_000,
  });

  const manufacturerRollupFilters = useMemo<PriceVarianceFilters>(() => {
    const next = { ...effectiveFilters };
    delete next.manufacturer_id;
    delete next.article;
    return next;
  }, [effectiveFilters]);
  const manufacturerRollupQuery = useQuery({
    queryKey: [
      "reports",
      "price-variance-grouped",
      "manufacturer-rollup",
      manufacturerRollupFilters,
    ],
    queryFn: () =>
      fetchPriceVarianceGrouped({
        ...manufacturerRollupFilters,
        group_by: "manufacturer",
        sort: "overpay_desc",
      }),
    enabled: rollupsEnabled,
    staleTime: 60_000,
  });

  const supplierRollupFilters = useMemo<PriceVarianceFilters>(() => {
    const next = { ...effectiveFilters };
    delete next.supplier;
    delete next.article;
    return next;
  }, [effectiveFilters]);
  const supplierRollupQuery = useQuery({
    queryKey: [
      "reports",
      "price-variance-grouped",
      "supplier-rollup",
      supplierRollupFilters,
    ],
    queryFn: () =>
      fetchPriceVarianceGrouped({
        ...supplierRollupFilters,
        group_by: "supplier",
        sort: "overpay_desc",
      }),
    enabled: rollupsEnabled,
    staleTime: 60_000,
  });

  const projectsQuery = useQuery({
    queryKey: ["projects", "linkable", "all"],
    queryFn: () =>
      fetchProjects({ activeOnly: false, limit: 1000, withOverpay: false }),
    staleTime: 300_000,
  });

  const projectByKey = useMemo<ReadonlyMap<string, ProjectSummary> | undefined>(
    () =>
      projectsQuery.data
        ? new Map(
            projectsQuery.data.items.map((project) => [project.project_key, project]),
          )
        : undefined,
    [projectsQuery.data],
  );

  const flatItems = useMemo(
    () => varianceQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [varianceQuery.data],
  );
  const flatTotal = varianceQuery.data?.pages[0]?.total ?? null;
  const flatHasData = varianceQuery.data !== undefined;
  // Gleiche Filter und Sortierung wie die Liste (#632-Review R2).
  const exportFilters = useMemo(() => ({ ...effectiveFilters, sort }), [effectiveFilters, sort]);

  const manufacturerOptions = useMemo<MultiSelectOption[]>(() => {
    return (manufacturerRollupQuery.data?.items ?? [])
      .filter((row) => row.manufacturer_id)
      .map((row) => ({
        key: row.manufacturer_id!,
        label: row.manufacturer_name ?? row.manufacturer_id!,
        searchText: `${row.manufacturer_name ?? ""} ${row.manufacturer_id}`,
        secondary: formatEuro(row.total_overpay),
      }));
  }, [manufacturerRollupQuery.data]);

  const supplierOptions = useMemo<MultiSelectOption[]>(() => {
    return (supplierRollupQuery.data?.items ?? [])
      .filter((row) => row.supplier)
      .map((row) => ({
        key: row.supplier!,
        label: row.supplier!,
        badge: (
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: filterColor("supplier", row.supplier!) }}
          />
        ),
        searchText: row.supplier!,
        secondary: formatEuro(row.total_overpay),
      }));
  }, [supplierRollupQuery.data]);

  const projectOptions = useMemo<MultiSelectOption[]>(() => {
    const items = [...(projectsQuery.data?.items ?? [])];
    items.sort(
      (a, b) => Number(b.total_spend ?? 0) - Number(a.total_spend ?? 0),
    );
    return items.map((project) => ({
      key: project.project_key,
      label: projectDisplay(project, project.project_key)?.title ?? project.project_key,
      searchText: projectSearchText(project, project.project_key),
      secondary: project.line_count
        ? `${project.line_count.toLocaleString("de-AT")} Z`
        : undefined,
    }));
  }, [projectsQuery.data]);

  return {
    referenceSummaryQuery,
    varianceQuery,
    groupedQuery,
    usersQuery,
    manufacturerRollupQuery,
    supplierRollupQuery,
    projectsQuery,
    projectByKey,
    flatItems,
    flatTotal,
    flatHasData,
    exportFilters,
    manufacturerOptions,
    supplierOptions,
    projectOptions,
  };
}
